"use client";

import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import { cleanFullName } from "@shared-libs/instagram/feed-posts";
import { createPortal } from "react-dom";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { useGetAllUpdatesForAccount } from "@/utils/react-query-hooks";
import { cn } from "@/lib/utils";
import { Table } from "@/types";
import { CartesianGrid, XAxis, YAxis, BarChart, Bar, Cell } from "recharts";
import { ChartConfig, ChartContainer, ChartTooltip } from "@/components/ui/chart";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import dayjs from "@/lib/dayjs";
import { toast } from "sonner";
import { DEMO_ACCOUNT_IDS } from "@/constants";
import { MobileTooltip, MobileTooltipContent, MobileTooltipTrigger } from "@/components/ui/mobile-tooltip";
import { motion, AnimatePresence } from "framer-motion";
import ProfilePreviewTooltip from "../ProfilePreviewTooltip";

// Icons
import { TiZoomOut } from "react-icons/ti";
import { ArrowSmallUpIcon, SparklesIcon, ArrowTrendingUpIcon } from "@heroicons/react/20/solid";
import { DownloadIcon } from "@/components/ui/download";
import { ChartLineIcon, type ChartLineIconHandle } from "@/components/ui/chart-line";

// Plan type to daily follower rate mapping
const PLAN_DAILY_RATES: Record<string, number> = {
  trial: 22,
  basic: 35,
  // Go tier: ~1000-2000/mo
  go: 35,
  pro: 85,
  // Grow tier: ~2500-3500/mo
  grow: 85,
  turbo: 117,
  // Glory tier: ~3500-5000/mo
  glory: 117
};
const GLORY_DAILY_RATE = 117;

// Speed setting overrides (if set in targeting_options)
const SPEED_DAILY_RATES: Record<string, number> = {
  slow: 22,
  normal: 50,
  turbo: 117
};

// Seeded pseudo-random for deterministic noise per day offset
function seededRandom(seed: number): number {
  const x = Math.sin(seed * 9301 + 49297) * 49271;
  return x - Math.floor(x);
}
export default function FollowersChart({
  account,
  isAnalyticPage = false,
  updates: providedUpdates
}: {
  account: Partial<Table<"accounts">>;
  isAnalyticPage?: boolean;
  updates?: any[];
}) {
  const chartId = useId();
  const iconRef = useRef<ChartLineIconHandle>(null);
  const [chartTimeFrameTab, setChartTimeFrameTab] = useState(0);
  const [chartViewMode, setChartViewMode] = useState<"forecast" | "history">("forecast");

  // Shadcn Chart Config - platform-specific colors
  const chartConfig = useMemo(() => ({
    gain: {
      label: "Daily Gain",
      color: account.platform === "tiktok" ? "hsl(348 79% 56%)" : "hsl(var(--chart-1))"
    },
    loss: {
      label: "Loss",
      color: "hsl(220 10% 72%)" // Neutral gray for losses
    }
  }) satisfies ChartConfig, [account.platform]);

  // Determine if we should fetch all updates
  const fetchAllUpdates = isAnalyticPage || chartTimeFrameTab === 1; // "All time" tab
  // ALWAYS call the query hook (Rules of Hooks) — never inside a ternary.
  // When updates are provided by the parent, pass "" so the query stays disabled
  // (enabled: !!accountId) and no wasted/failing network request fires.
  const usingProvidedUpdates = providedUpdates !== undefined;
  const {
    data: fetchedUpdates,
    isLoading: rawIsLoadingUpdates,
    isError: rawIsUpdatesError,
    refetch: rawRefetch
  } = useGetAllUpdatesForAccount(usingProvidedUpdates ? "" : account.id || "", fetchAllUpdates);
  // Provided-updates path has no fetch → never loading, never errored, refetch is a no-op
  const isLoadingUpdates = usingProvidedUpdates ? false : rawIsLoadingUpdates;
  const isUpdatesError = usingProvidedUpdates ? false : rawIsUpdatesError;
  const refetch = usingProvidedUpdates ? () => {} : rawRefetch;

  // Use provided updates if available, otherwise use fetched updates
  const updates = useMemo(() => {
    const baseUpdates = providedUpdates || fetchedUpdates || [];

    // If no updates, add a default update
    if (baseUpdates.length === 0) {
      return Array(2).fill({
        account_id: account.id,
        created_at: new Date().toISOString(),
        followers: account.followers,
        followings: 0,
        id: "default-id"
      });
    }
    // If only one update, add a default update to the beginning of the array
    else if (baseUpdates.length === 1) {
      return [{
        account_id: account.id,
        created_at: new Date().toISOString(),
        followers: account.followers,
        followings: 0,
        id: "default-id"
      }, ...baseUpdates];
    }
    return baseUpdates;
  }, [providedUpdates, fetchedUpdates, account.id]);

  // Last updated time
  const [lastUpdated, setLastUpdated] = useState<string | null>(null);
  useEffect(() => {
    if (isAnalyticPage) {
      const latestUpdate = updates[updates.length - 1].created_at;
      // if latestUpdate is today, show 1 hour ago, 2 hours ago, etc.
      if (dayjs(latestUpdate).isSame(dayjs(), "day")) setLastUpdated("1 hour ago");else setLastUpdated(dayjs(latestUpdate).fromNow());
    }
  }, [updates]);
  const isDemoAccount = account && DEMO_ACCOUNT_IDS.includes(account.id);
  const [isHoveringChart, setIsHoveringChart] = useState<boolean>(false);
  const [hoveredBar, setHoveredBar] = useState(null);
  const [historyHoveredBar, setHistoryHoveredBar] = useState(null);
  const [activeBarIndex, setActiveBarIndex] = useState<number | null>(null);

  // Persistent tooltip state - position calculated from activeBarIndex
  const [tooltipPos, setTooltipPos] = useState<{
    x: number;
    y: number;
  } | null>(null);
  const chartContainerRef = useRef<HTMLDivElement>(null);

  // Track tooltip visibility for entrance animation (ref declared here, computed after forecastData)
  const wasTooltipVisibleRef = useRef(false);

  // Preload avatar image on mount to prevent network requests on every tooltip hover
  const avatarUrlRef = useRef(account.avatar_url || "/img/default_avatar.jpeg");
  useEffect(() => {
    const img = new Image();
    img.src = avatarUrlRef.current;
  }, []);

  // Memoize static profile data to prevent re-renders
  const profileData = useMemo(() => ({
    avatarUrl: account.avatar_url || "/img/default_avatar.jpeg",
    username: account.username || "",
    fullName: cleanFullName(account.name),
    // 31 accounts stored the supplier's "Access delayed" placeholder as their name
    postsCount: account.medias ?? undefined,
    followingCount: account.followings ?? undefined,
    isVerified: account.is_verified ?? false
  }), [account.avatar_url, account.username, account.name, account.medias, account.followings, account.is_verified]);

  // Responsive: fewer bars on mobile for touch-friendliness
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 640);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  // Track direction for title animation (1 = right/forward, -1 = left/back)
  const [slideDirection, setSlideDirection] = useState<number>(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const prevTabRef = useRef(chartTimeFrameTab);
  const prevViewModeRef = useRef<"forecast" | "history">("forecast");

  // Define tabs
  const tabs = isAnalyticPage ? ["All time", "Cumulative"] : ["Daily", "Weekly"];

  // Data time frame
  const searchParams = useSearchParams();
  const [selectedTimeFrame, setSelectedTimeFrame] = useState<string>("365y");
  useEffect(() => {
    const dQueryParam = new URLSearchParams(window.location.search).get("d");
    const defaultTimeFrame = "365y";
    const timeFrame = dQueryParam ? dQueryParam : defaultTimeFrame;
    setSelectedTimeFrame(timeFrame as any);
  }, [searchParams]);
  let startDate = dayjs().subtract(30, "days");
  const timeFrameValue = parseInt(selectedTimeFrame.slice(0, -1));
  const timeFrameUnit = selectedTimeFrame.slice(-1);
  switch (timeFrameUnit) {
    case "d":
      startDate = dayjs().subtract(timeFrameValue, "days");
      break;
    case "m":
      startDate = dayjs().subtract(timeFrameValue, "months");
      break;
    case "y":
      startDate = dayjs().subtract(timeFrameValue, "years");
      break;
    default:
      startDate = dayjs().subtract(30, "days");
    // Default to 30 days
  }
  const router = useRouter();
  const pathName = usePathname();
  const changeDateRange = (timeFrame_0: string) => {
    const newSearchParams = new URLSearchParams(window.location.search);
    timeFrame_0 = timeFrame_0 || "365y";
    newSearchParams.set("d", timeFrame_0);
    router.push(`${pathName}?${newSearchParams.toString()}`, undefined);
  };
  const delta = account.starting_followers > 0 ? (account.followers - account.starting_followers) / account.starting_followers * 100 : 0;

  // Helper: Get ISO week info
  const getWeekInfo = (date: dayjs.Dayjs) => {
    const weekStart = date.startOf('week');
    const weekEnd = date.endOf('week');
    return {
      weekKey: weekStart.format('YYYY-MM-DD'),
      weekStart,
      weekEnd,
      label: `${weekStart.format('MMM D')}-${weekEnd.format('D')}`
    };
  };

  // For demo accounts, generate realistic data - MEMOIZED
  const demoChartData = useMemo(() => {
    if (!isDemoAccount) return null;
    const isWeeklyMode = chartTimeFrameTab === 1;
    const startingFollowers = account.starting_followers || account.followers * 0.95;
    const currentFollowers = account.followers;
    const totalGrowth = currentFollowers - startingFollowers;
    if (isWeeklyMode) {
      // Generate 4 weeks of data for weekly mode - with upward trend
      const avgWeeklyGain = totalGrowth / 4;
      return Array.from({
        length: 4
      }, (_, i) => {
        const weekEnd_0 = new Date();
        weekEnd_0.setDate(weekEnd_0.getDate() - (3 - i) * 7);
        const weekStart_0 = new Date(weekEnd_0);
        weekStart_0.setDate(weekStart_0.getDate() - 6);
        const weekStartStr = weekStart_0.toLocaleDateString("en-US", {
          month: "short",
          day: "numeric"
        });
        const weekEndStr = weekEnd_0.toLocaleDateString("en-US", {
          day: "numeric"
        });
        const label = `${weekStartStr}-${weekEndStr}`;

        // Oscillation - deterministic weekly variation
        const seed = (i * 11 + 5) % 10;
        const oscillation = 0.8 + seed / 10 * 0.4; // 0.8-1.2 range

        // Upward trend - starts at 0.6x, ends at 1.5x (accelerating growth)
        const trendMultiplier = 0.6 + i / 3 * 0.9;
        const weeklyGain = Math.round(avgWeeklyGain * oscillation * trendMultiplier);
        return {
          name: label,
          gain: weeklyGain,
          isPartial: false
        };
      });
    } else {
      // Daily mode - oscillating with upward trend
      // Generate 30 entries (max needed for desktop chart width)
      const numDays = 30;
      const avgDailyGain = totalGrowth / numDays;

      // First pass: generate all gains with oscillation + trend
      const entries: Array<{
        name: string;
        gain: number;
      }> = [];
      let maxGain = 0;
      for (let i_0 = 0; i_0 < numDays; i_0++) {
        const date_0 = new Date();
        date_0.setDate(date_0.getDate() - (numDays - 1 - i_0));
        const name = date_0.toLocaleDateString("en-US", {
          month: "short",
          day: "numeric"
        });

        // Oscillation - deterministic daily variation (creates natural peaks/valleys)
        const seed_0 = (i_0 * 7 + 3) % 10;
        const oscillation_0 = 0.6 + seed_0 / 10 * 0.8; // 0.6-1.4 range

        // Upward trend - starts at 0.5x, ends at 1.5x (accelerating growth)
        const trendMultiplier_0 = 0.5 + i_0 / (numDays - 1) * 1.0;
        const dailyGain = Math.round(avgDailyGain * oscillation_0 * trendMultiplier_0);
        maxGain = Math.max(maxGain, dailyGain);
        entries.push({
          name,
          gain: dailyGain
        });
      }

      // Ensure last bar (today) is the tallest - adds 15% above previous max
      const lastEntry = entries[entries.length - 1];
      lastEntry.gain = Math.round(maxGain * 1.15);
      return entries;
    }
  }, [isDemoAccount, account.starting_followers, account.followers, chartTimeFrameTab]);

  // Calculate daily/weekly gains from actual follower data - MEMOIZED
  const chartData = useMemo(() => {
    if (isDemoAccount) return demoChartData;
    const isWeeklyMode_0 = chartTimeFrameTab === 1;
    const filteredUpdates = updates.filter(update => dayjs(update.created_at).isAfter(startDate)).sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
    if (isWeeklyMode_0) {
      // Weekly aggregation
      const weeklyData = new Map<string, {
        weekStart: dayjs.Dayjs;
        weekEnd: dayjs.Dayjs;
        totalGain: number;
        days: number;
      }>();
      filteredUpdates.slice(1).forEach((update_0, i_1) => {
        const date_1 = dayjs(update_0.created_at);
        const weekInfo = getWeekInfo(date_1);
        const previousFollowers = filteredUpdates[i_1].followers;
        const currentFollowers_0 = update_0.followers;
        const dailyGain_0 = currentFollowers_0 - previousFollowers;
        if (!weeklyData.has(weekInfo.weekKey)) {
          weeklyData.set(weekInfo.weekKey, {
            weekStart: weekInfo.weekStart,
            weekEnd: weekInfo.weekEnd,
            totalGain: 0,
            days: 0
          });
        }
        const week = weeklyData.get(weekInfo.weekKey)!;
        week.totalGain += dailyGain_0;
        week.days += 1;
      });

      // Convert to array and format
      return Array.from(weeklyData.entries()).sort((a_0, b_0) => a_0[1].weekStart.valueOf() - b_0[1].weekStart.valueOf()).map(([weekKey, data]) => {
        const isPartial = data.days < 7;
        const label_0 = `${data.weekStart.format('MMM D')}-${data.weekEnd.format('D')}`;
        return {
          name: label_0,
          gain: data.totalGain,
          isPartial
        };
      });
    } else {
      // Daily mode - existing logic
      return filteredUpdates.slice(1).map((update_1, i_2) => {
        const date_2 = dayjs(update_1.created_at);
        const formattedDate = date_2.format("MMM D");
        const previousFollowers_0 = filteredUpdates[i_2].followers;
        const currentFollowers_1 = update_1.followers;
        const dailyGain_1 = currentFollowers_1 - previousFollowers_0;
        return {
          name: formattedDate,
          gain: dailyGain_1
        };
      });
    }
  }, [isDemoAccount, demoChartData, updates, startDate, chartTimeFrameTab]);

  // Detect outlier gains (e.g., data corrections) that distort chart scale
  // An entry is an outlier if its absolute gain > 5x the median AND > 1000
  const outlierIndices = useMemo(() => {
    if (!chartData || chartData.length < 3) return new Set<number>();
    const absGains = chartData.map(d => Math.abs(d.gain)).filter(g => g > 0);
    if (absGains.length < 2) return new Set<number>();
    const sorted = [...absGains].sort((a_1, b_1) => a_1 - b_1);
    const median = sorted[Math.floor(sorted.length / 2)];
    const indices = new Set<number>();
    chartData.forEach((d_0, i_3) => {
      if (Math.abs(d_0.gain) > median * 5 && Math.abs(d_0.gain) > 1000) {
        indices.add(i_3);
      }
    });
    return indices;
  }, [chartData]);

  // Calculate average daily gain (always in "per day" format)
  const avgDailyGain_0 = useMemo(() => {
    if (!chartData || chartData.length === 0) return 0;
    const totalGain = chartData.reduce((sum, item) => sum + item.gain, 0);
    const isWeeklyMode_1 = chartTimeFrameTab === 1;
    if (isWeeklyMode_1) {
      // Convert weekly totals to daily average
      const totalDays = chartData.length * 7;
      return Math.round(totalGain / totalDays);
    } else {
      return Math.round(totalGain / chartData.length);
    }
  }, [chartData, chartTimeFrameTab]);

  // Check if this is a truly new account that should see the demo/projection view
  // Only show demo for: recently created + not started + not paid + no growth data
  const isNewAccount = useMemo(() => {
    if (isDemoAccount) return false;

    // If account is active (growth running), never show demo
    if (account.active === true) return false;

    // If account has a subscription (paid), never show demo
    if (account.subscription_id) return false;

    // If account is not trial type, never show demo
    if (account.account_type && account.account_type !== "trial") return false;

    // Check if account was created recently (within 7 days)
    const accountCreatedAt = account.created_at ? new Date(account.created_at) : null;
    const isRecentlyCreated = accountCreatedAt && Date.now() - accountCreatedAt.getTime() < 7 * 24 * 60 * 60 * 1000;

    // Check if there's no meaningful growth data
    const hasNoGrowth = !chartData || chartData.length === 0 || chartData.length <= 2 && chartData.every(d_1 => d_1.gain === 0);
    return isRecentlyCreated && hasNoGrowth;
  }, [isDemoAccount, account.active, account.subscription_id, account.account_type, account.created_at, chartData]);

  // Check if there's meaningful history data for the "History" tab
  const hasHistoryData = useMemo(() => {
    if (!chartData || chartData.length === 0) return false;
    return chartData.some(d_2 => d_2.gain !== 0);
  }, [chartData]);

  // Cap outlier values for history view display while preserving real values for tooltips
  const historyDisplayData = useMemo(() => {
    if (!chartData || chartData.length < 3) return chartData?.map(d_3 => ({
      ...d_3,
      displayGain: d_3.gain,
      actualGain: d_3.gain,
      isOutlier: false
    })) || [];

    // Find the max non-outlier absolute gain
    const nonOutlierGains = chartData.filter((__0, i_4) => !outlierIndices.has(i_4)).map(d_4 => Math.abs(d_4.gain));
    if (nonOutlierGains.length === 0) return chartData.map(d_5 => ({
      ...d_5,
      displayGain: d_5.gain,
      actualGain: d_5.gain,
      isOutlier: false
    }));
    const maxNonOutlier = Math.max(...nonOutlierGains);
    const capValue = maxNonOutlier * 2; // Cap at 2x the max non-outlier

    return chartData.map((d_6, i_5) => ({
      ...d_6,
      displayGain: outlierIndices.has(i_5) ? Math.sign(d_6.gain) * Math.min(Math.abs(d_6.gain), capValue) : d_6.gain,
      actualGain: d_6.gain,
      isOutlier: outlierIndices.has(i_5)
    }));
  }, [chartData, outlierIndices]);

  // Compute the daily rate for this account based on plan + speed setting
  const accountDailyRate = useMemo(() => {
    const speed = (account as any)?.targeting_options?.general?.speed;
    if (speed && SPEED_DAILY_RATES[speed]) return SPEED_DAILY_RATES[speed];
    return PLAN_DAILY_RATES[account.account_type || "trial"] || 22;
  }, [account]);

  // Stable seed derived from account ID (doesn't change across refreshes)
  const accountSeed = useMemo(() => {
    if (!account.id) return 42;
    return account.id.split("").reduce((acc, c) => acc + c.charCodeAt(0), 0);
  }, [account.id]);

  // Build the 30-day growth forecast data (for non-analytics pages)
  // Each entry has: `value` (main bar), `turboExtra` (stacked extension), `projectedTotal` (cumulative)
  // Note: Demo accounts now use forecast chart too (same as production) for consistent UX
  const forecastData = useMemo(() => {
    if (isAnalyticPage) return null;
    const today = dayjs();
    const currentFollowers_2 = account.followers || 0;
    const days: Array<{
      name: string;
      value: number;
      turboExtra: number;
      turboTotal: number;
      projectedTotal: number;
      type: "actual" | "projected" | "today" | "empty";
    }> = [];
    const isAlreadyTurbo = account.account_type === "turbo" || account.account_type === "glory";

    // Responsive bar count: 14 on mobile, 30 on desktop
    const totalBars = isMobile ? 14 : 30;

    // DEMO ACCOUNTS: Generate a full impressive chart with all data as "projected"
    // so ProfilePreviewTooltip shows on hover (not simple tooltip for "actual" type)
    if (isDemoAccount && demoChartData) {
      const startingFollowers_0 = account.starting_followers || currentFollowers_2 * 0.92;
      let runningTotal = startingFollowers_0;

      // Use demo data for most bars, but ensure we fill the entire chart
      const demoEntries = demoChartData.slice(-totalBars); // Take last N entries

      demoEntries.forEach((entry, i_6) => {
        runningTotal += entry.gain;
        const isLastBar = i_6 === demoEntries.length - 1;
        days.push({
          name: entry.name,
          value: entry.gain,
          turboExtra: 0,
          // No upsell for demo
          turboTotal: entry.gain,
          projectedTotal: Math.round(runningTotal),
          // Last bar is "today", rest are "projected" - all get ProfilePreviewTooltip
          type: isLastBar ? "today" : "projected"
        });
      });
      return days;
    }

    // PRODUCTION ACCOUNTS: Original logic
    // Determine how many actual days we have
    const actualDays = chartData?.length || 0;
    const pastDaysToShow = Math.min(actualDays, totalBars - 1); // cap to leave room for today

    // Fill past actual days - only include days with positive growth
    // Days with zero/negative gains (e.g., account losing followers) are skipped
    // to avoid confusing empty bars before the projected growth section
    // Outlier spikes (data corrections) are also skipped to prevent scale distortion
    if (chartData && chartData.length > 0) {
      const startIdx = Math.max(0, chartData.length - pastDaysToShow);
      for (let i_7 = startIdx; i_7 < chartData.length; i_7++) {
        const gain = chartData[i_7].gain;
        // Skip days with zero/negative growth or outlier spikes
        if (gain <= 0 || outlierIndices.has(i_7)) continue;
        const date_3 = chartData[i_7].name; // Already formatted as "MMM D"
        days.push({
          name: date_3,
          value: gain,
          turboExtra: 0,
          turboTotal: 0,
          projectedTotal: 0,
          type: "actual"
        });
      }
    }

    // If all slots used by actual data, return as-is
    if (days.length >= totalBars) return days;

    // Add "today" bar — solid green anchor showing "you are here"
    const todayRampFactor = 0.3;
    const todayNoise = 1 + (seededRandom(accountSeed * 1000 + 999) - 0.5) * 0.3;
    const todayGain = Math.round(accountDailyRate * todayRampFactor * todayNoise);
    const todayGloryGain = Math.round(GLORY_DAILY_RATE * todayRampFactor * todayNoise);
    const todayTurboExtra = isAlreadyTurbo ? 0 : Math.max(0, todayGloryGain - todayGain);
    days.push({
      name: today.format("MMM D"),
      value: todayGain,
      turboExtra: todayTurboExtra,
      turboTotal: isAlreadyTurbo ? todayGain : todayGloryGain,
      projectedTotal: currentFollowers_2 + todayGain,
      type: "today"
    });

    // Fill remaining slots with projected days (tomorrow onward)
    const remainingSlots = totalBars - days.length;
    let cumulativeGain = todayGain;
    for (let i_8 = 0; i_8 < remainingSlots; i_8++) {
      const seed_1 = accountSeed * 1000 + i_8;
      const rampFactor = Math.min(1, 0.3 + (i_8 + 1) / 14 * 0.7);
      const noise = 1 + (seededRandom(seed_1) - 0.5) * 0.3;
      const projectedGain = Math.round(accountDailyRate * rampFactor * noise);
      const turboGain = Math.round(GLORY_DAILY_RATE * rampFactor * noise);
      const turboExtra = isAlreadyTurbo ? 0 : Math.max(0, turboGain - projectedGain);
      cumulativeGain += projectedGain;
      days.push({
        name: dayjs().add(i_8 + 1, "day").format("MMM D"),
        value: projectedGain,
        turboExtra,
        turboTotal: isAlreadyTurbo ? projectedGain : turboGain,
        projectedTotal: currentFollowers_2 + cumulativeGain,
        type: "projected"
      });
    }
    return days;
  }, [isAnalyticPage, isDemoAccount, demoChartData, chartData, outlierIndices, accountDailyRate, account.account_type, accountSeed, account.followers, account.starting_followers, isMobile]);

  // Tooltip visibility - computed after forecastData is available
  const isTooltipVisible = activeBarIndex !== null && forecastData[activeBarIndex]?.type !== "empty" && forecastData[activeBarIndex]?.type !== "actual";

  // Update visibility ref for entrance detection (entrance gets animation, updates are instant)
  useEffect(() => {
    wasTooltipVisibleRef.current = isTooltipVisible;
  }, [isTooltipVisible]);

  // Calculate tooltip position when activeBarIndex changes - uses viewport coords for portal
  useEffect(() => {
    if (activeBarIndex === null || !chartContainerRef.current) {
      // Don't reset tooltipPos - keep last position so tooltip stays mounted (hidden via CSS)
      return;
    }
    // Query all bar rectangles - Recharts renders them as path elements in .recharts-bar-rectangle
    const barElements = chartContainerRef.current.querySelectorAll(".recharts-bar-rectangle");
    const barElement = barElements[activeBarIndex];
    if (barElement) {
      const rect = barElement.getBoundingClientRect();
      // Use viewport coordinates directly (portal renders at body level)
      setTooltipPos({
        x: rect.left + rect.width / 2,
        y: rect.top - 10
      });
    }
  }, [activeBarIndex]); // Only re-run when activeBarIndex changes

  const ScreenshotButton = <button aria-label="Download chart as image" className="ignore hidden h-11 w-11 items-center justify-center rounded-xl bg-gray-100 text-gray-500 transition-colors duration-150 hover:bg-gray-200 hover:text-gray-700 sm:inline-flex" onClick={async () => {
    toast.loading("Generating chart...");
    const html2canvas = (await import("html2canvas")).default;
    const chartElement = document.getElementById("followers-growth-chart");
    if (chartElement) {
      html2canvas(chartElement, {
        ignoreElements: node => node.classList.contains("ignore")
      }).then(canvas => {
        const link = document.createElement("a");
        link.download = `${account.username}-followers-growth.png`;
        link.href = canvas.toDataURL();
        link.click();
        toast.dismiss();
        toast.success("Chart downloaded!");
      });
    }
  }}>
            <DownloadIcon size={16} />
        </button>;

  // Custom tooltip for daily/weekly gains
  // Uses actualGain (real value) for display when bar is capped due to outlier detection
  const CustomTooltip = ({
    active,
    payload,
    label: label_1
  }: any) => {
    if (active && payload && payload.length) {
      const entry_0 = payload[0].payload;
      // Use actualGain if available (history view with outlier capping), otherwise use raw value
      const gain_0 = entry_0?.actualGain ?? payload[0].value;
      const isPartial_0 = entry_0?.isPartial;
      const isOutlier = entry_0?.isOutlier;
      const isPositive = gain_0 >= 0;
      return <div className="rounded-lg border border-gray-200 bg-white p-2.5 shadow-lg">
                    <p className="text-xs font-medium text-gray-600">
                        {label_1}
                        {isPartial_0 && <span className="ml-1 text-gray-400">(partial)</span>}
                        {isOutlier && <span className="ml-1 text-amber-500">(correction)</span>}
                    </p>
                    <p className={cn("text-sm font-bold", isPositive ? "text-emerald-600" : "text-gray-500")}>
                        {isPositive ? "+" : ""}{gain_0.toLocaleString()} followers
                    </p>
                </div>;
    }
    return null;
  };
  return <>
            {isUpdatesError ?
    // Inline error state — only reachable for real (non-demo, non-provided) accounts.
    // Avoids the infinite skeleton / misleading flat chart when the fetch fails.
    <div className="flex min-h-[300px] flex-col items-center justify-center px-4 py-10 text-center">
                    <p className="text-base font-medium text-gray-900">Couldn&apos;t load your growth data</p>
                    <p className="mt-1 max-w-[260px] text-[13px] leading-relaxed text-gray-500">Something went wrong while fetching your follower history.</p>
                    <button type="button" onClick={() => refetch()} className="mt-4 inline-flex items-center justify-center rounded-lg bg-[#141414] px-4 py-2 text-[13px] font-semibold text-white transition-colors duration-150 hover:bg-[#141414]/90">
                        Retry
                    </button>
                </div> : !updates || !chartData || isLoadingUpdates ?
    // Skeleton loader - mirrors exact loaded layout structure
    <>
                    {/* Header skeleton - matches loaded header: items-start, nested left column groups title + metric */}
                    <div className="px-4 pb-0 pt-3">
                        <div className="flex items-start justify-between">
                            {/* Left Column: Title + Metric (matches loaded <div> wrapper) */}
                            <div>
                                {/* Title skeleton - matches h3 text-base */}
                                <div className="h-5 w-36 animate-pulse rounded bg-gray-100" />
                                {/* Metric row skeleton - matches mt-1 flex items-center gap-2.5 */}
                                <div className="mt-1 flex items-center gap-2.5">
                                    <div className="h-8 w-24 animate-pulse rounded bg-gray-100" />
                                    <div className="h-[26px] w-20 animate-pulse rounded-full bg-gray-100" />
                                </div>
                            </div>
                            
                            {/* Controls skeleton: download button (hidden mobile) + tabs */}
                            <div className="flex items-center gap-1.5 sm:gap-2">
                                <div className="hidden h-11 w-11 animate-pulse rounded-xl bg-gray-100 sm:block" />
                                <div className="h-9 w-[150px] animate-pulse rounded-lg bg-gray-100" />
                            </div>
                        </div>
                    </div>

                    {/* Chart skeleton - matches loaded content area: px-4 pb-3 pt-3 */}
                    <div className="px-4 pb-3 pt-3">
                        <div className="h-[220px] w-full animate-pulse rounded-lg bg-gray-50">
                            {/* Simulated bar chart skeleton */}
                            <div className="flex h-full items-end justify-around gap-2 px-4 pb-6 pt-4">
                                {[65, 45, 80, 55, 70, 40, 85, 60, 50, 75, 45, 90].map((h, i_9) => <div key={i_9} className="w-full max-w-[40px] animate-pulse rounded-t bg-gray-100" style={{
              height: `${h}%`
            }} />)}
                            </div>
                        </div>
                    </div>
                </> : (
    /* 
     * SKELETON LOADING PATTERN: Content uses opacity-only fade (no transforms)
     * to prevent layout shifts when transitioning from skeleton state.
     */
    <div className="animate-fade-in-gentle">
                    {/* Card Header - Clean, consumer-friendly */}
                    <div className="px-4 pb-0 pt-3" onMouseEnter={() => iconRef.current?.startAnimation()} onMouseLeave={() => iconRef.current?.stopAnimation()}>
                        {/* Top Row: Title + Controls */}
                        <div className="flex items-start justify-between">
                            {/* Left Column: Title + Metric */}
                            <div>
                                <h3 className="text-balance relative text-base font-medium text-gray-900">
                                    {/* Gradient fade overlays - instant ON, slow fade OFF */}
                                    <span className="pointer-events-none absolute left-0 top-0 bottom-0 z-10 w-5 bg-gradient-to-r from-white to-transparent" style={{
                opacity: isAnimating ? 1 : 0,
                transition: isAnimating ? "none" : "opacity 250ms ease-out"
              }} />
                                    <span className="pointer-events-none absolute right-0 top-0 bottom-0 z-10 w-5 bg-gradient-to-l from-white to-transparent" style={{
                opacity: isAnimating ? 1 : 0,
                transition: isAnimating ? "none" : "opacity 250ms ease-out"
              }} />

                                    <span className="block overflow-hidden">
                                        <AnimatePresence mode="wait" initial={false} custom={slideDirection}>
                                            <motion.span key={isAnalyticPage ? chartTimeFrameTab === 1 ? "weekly" : "daily" : chartViewMode} custom={slideDirection} variants={{
                    initial: (dir: number) => ({
                      x: dir === 0 ? 0 : dir > 0 ? -20 : 20,
                      opacity: 0
                    }),
                    animate: {
                      x: 0,
                      opacity: 1
                    },
                    exit: (dir_0: number) => ({
                      x: dir_0 > 0 ? 20 : -20,
                      opacity: 0
                    })
                  }} initial="initial" animate="animate" exit="exit" transition={{
                    duration: 0.25,
                    ease: [0.22, 1, 0.36, 1]
                  }} onAnimationComplete={() => setIsAnimating(false)} className="inline-block">
                                                {isAnalyticPage ? chartTimeFrameTab === 1 ? "New Followers Per Week" : "New Followers Per Day" : chartViewMode === "history" ? "Growth History" : "Follower Growth"}
                                            </motion.span>
                                        </AnimatePresence>
                                    </span>
                                </h3>

                                {/* Metric Row: Big Number + Status Badge - directly under title */}
                                <div className="mt-1 flex items-center gap-2.5 flex-wrap">
                                    <span className="tabular-nums text-2xl font-semibold tracking-tight text-gray-900">{(account.followers || 0).toLocaleString()}</span>

                                    {/* Show AI analyzed badge for new accounts, otherwise show delta badge */}
                                    {isNewAccount ? <div className="inline-flex h-[26px] items-center gap-1.5 rounded-full bg-emerald-50 px-2.5 text-[12px] font-medium text-emerald-700">
                                            <SparklesIcon className="h-3.5 w-3.5 text-emerald-500" />
                                            <span>AI analyzed @{account.username}</span>
                                        </div> : <div className={cn("inline-flex h-[26px] items-center gap-1 rounded-full px-2.5 text-[12px] font-medium", delta >= 0 ? "bg-emerald-50 to-emerald-500" : "bg-amber-50 to-amber-500")}>
                                            {delta == 0 && <>
                                                    <SparklesIcon className="h-3.5 w-3.5" />
                                                    <span>Just started</span>
                                                </>}
                                            {delta > 0 && <>
                                                    <ArrowSmallUpIcon className="h-3.5 w-3.5" />
                                                    <span>{delta < 10 ? delta.toFixed(2) : delta.toFixed(1)}%</span>
                                                </>}
                                            {delta < 0 && <>
                                                    <SparklesIcon className="h-3.5 w-3.5" />
                                                    <span>Optimizing</span>
                                                </>}
                                        </div>}

                                    {avgDailyGain_0 > 0 && <div className="inline-flex h-[26px] items-center gap-1 rounded-full bg-emerald-50 px-2.5 text-[12px] font-medium text-emerald-600">
                                            <ArrowTrendingUpIcon className="h-3.5 w-3.5" />
                                            <span>Avg +{avgDailyGain_0.toLocaleString()}/day</span>
                                        </div>}
                                </div>
                            </div>

                            {/* Controls: Download + Tabs */}
                            <div className="flex items-center gap-1.5 sm:gap-2">
                                {lastUpdated && <span className="hidden text-[11px] text-gray-400 sm:inline">Updated {lastUpdated}</span>}
                                <MobileTooltip>
                                    <MobileTooltipTrigger asChild>{ScreenshotButton}</MobileTooltipTrigger>
                                    <MobileTooltipContent side="bottom" className="max-w-[200px] shadow-lg">
                                        Download chart
                                    </MobileTooltipContent>
                                </MobileTooltip>

                                {/* Tab Switcher - only show on analytics page (Growth Overview uses fixed 30-day forecast) */}
                                {isAnalyticPage && <Tabs value={String(chartTimeFrameTab)} onValueChange={value => {
              const newTab = Number(value);

              // Track direction for title slide animation
              const direction = newTab > prevTabRef.current ? 1 : -1;
              setSlideDirection(direction);
              setIsAnimating(true);
              prevTabRef.current = newTab;
              setChartTimeFrameTab(newTab);
              if (isAnalyticPage && newTab === 1) {
                refetch();
              }
            }}>
                                        <TabsList variant="default" animatedIndicator>
                                            {tabs.map((tab, index) => <TabsTrigger key={index} value={String(index)} className="text-dashboard-xs">
                                                    {tab}
                                                </TabsTrigger>)}
                                        </TabsList>
                                    </Tabs>}

                                {/* View Mode Switcher - Growth/History on non-analytics pages */}
                                {!isAnalyticPage && <Tabs value={chartViewMode} onValueChange={value_0 => {
              const newMode = value_0 as "forecast" | "history";
              // Track direction: forecast→history = forward (1), history→forecast = back (-1)
              const direction_0 = newMode === "history" ? 1 : -1;
              setSlideDirection(direction_0);
              setIsAnimating(true);
              prevViewModeRef.current = newMode;
              setChartViewMode(newMode);
            }}>
                                        <TabsList variant="default" animatedIndicator>
                                            <TabsTrigger value="forecast" className="text-dashboard-xs">
                                                Growth
                                            </TabsTrigger>
                                            <TabsTrigger value="history" className="text-dashboard-xs">
                                                History
                                            </TabsTrigger>
                                        </TabsList>
                                    </Tabs>}
                            </div>
                        </div>
                    </div>

                    {/* Content area */}
                    <div className="px-4 pb-3 pt-3">
                        {/* Chart Section */}
                        <div id="followers-growth-chart">
                            {!isAnalyticPage && chartViewMode === "forecast" && forecastData ? (/* Growth Forecast Chart - stacked bars: value + turboExtra extension */
          <div ref={chartContainerRef} className="relative" onTouchStart={e => {
            const touch = e.touches[0];
            const rect_0 = e.currentTarget.getBoundingClientRect();
            const x = touch.clientX - rect_0.left;
            const barCount = forecastData.length;
            const index_0 = Math.min(barCount - 1, Math.max(0, Math.floor(x / rect_0.width * barCount)));
            setActiveBarIndex(index_0);
          }} onTouchMove={e_0 => {
            const touch_0 = e_0.touches[0];
            const rect_1 = e_0.currentTarget.getBoundingClientRect();
            const x_0 = touch_0.clientX - rect_1.left;
            const barCount_0 = forecastData.length;
            const index_1 = Math.min(barCount_0 - 1, Math.max(0, Math.floor(x_0 / rect_1.width * barCount_0)));
            setActiveBarIndex(index_1);
          }} onTouchEnd={() => {
            setTimeout(() => setActiveBarIndex(null), 1500);
          }} style={{
            touchAction: "pan-y"
          }}>
                                <ChartContainer config={chartConfig} className="aspect-auto h-[220px] w-full">
                                    <BarChart data={forecastData} margin={{
                left: 10,
                right: 10,
                top: 10,
                bottom: 0
              }} barCategoryGap={isMobile ? "20%" : "25%"} onMouseMove={state => {
                if (state && state.activeTooltipIndex !== undefined) {
                  setActiveBarIndex(state.activeTooltipIndex);
                }
              }} onMouseLeave={() => setActiveBarIndex(null)}>
                                        <defs>
                                            {/* Solid gradient for actual past gains */}
                                            <linearGradient id={`gradient-gain-${chartId}`} x1="0" y1="0" x2="0" y2="1">
                                                <stop offset="0%" stopColor={account.platform === "tiktok" ? "hsl(348 79% 56%)" : "hsl(160 70% 42%)"} stopOpacity={1} />
                                                <stop offset="100%" stopColor={account.platform === "tiktok" ? "hsl(348 79% 56%)" : "hsl(160 70% 35%)"} stopOpacity={0.85} />
                                            </linearGradient>
                                            {/* Striped pattern for projected future bars */}
                                            <pattern id={`pattern-projected-${chartId}`} patternUnits="userSpaceOnUse" width="6" height="6" patternTransform="rotate(45)">
                                                <rect width="3" height="6" fill={account.platform === "tiktok" ? "hsl(348 79% 56%)" : "hsl(160 70% 42%)"} fillOpacity="0.55" />
                                                <rect x="3" width="3" height="6" fill={account.platform === "tiktok" ? "hsl(348 79% 56%)" : "hsl(160 70% 42%)"} fillOpacity="0.2" />
                                            </pattern>
                                            {/* Very faint fill for glory extension on top */}
                                            <linearGradient id={`gradient-turbo-ext-${chartId}`} x1="0" y1="0" x2="0" y2="1">
                                                <stop offset="0%" stopColor={account.platform === "tiktok" ? "hsl(348 79% 56%)" : "hsl(160 70% 42%)"} stopOpacity={0.15} />
                                                <stop offset="100%" stopColor={account.platform === "tiktok" ? "hsl(348 79% 56%)" : "hsl(160 70% 35%)"} stopOpacity={0.08} />
                                            </linearGradient>
                                            {/* Solid gradient for "today" bar - vibrant green anchor */}
                                            <linearGradient id={`gradient-today-${chartId}`} x1="0" y1="0" x2="0" y2="1">
                                                <stop offset="0%" stopColor={account.platform === "tiktok" ? "hsl(348 84% 59%)" : "hsl(160 84% 39%)"} stopOpacity={1} />
                                                <stop offset="100%" stopColor={account.platform === "tiktok" ? "hsl(348 84% 52%)" : "hsl(160 84% 32%)"} stopOpacity={0.9} />
                                            </linearGradient>
                                            {/* Hover variant of today - brighter */}
                                            <linearGradient id={`gradient-today-hover-${chartId}`} x1="0" y1="0" x2="0" y2="1">
                                                <stop offset="0%" stopColor={account.platform === "tiktok" ? "hsl(348 90% 62%)" : "hsl(160 90% 42%)"} stopOpacity={1} />
                                                <stop offset="100%" stopColor={account.platform === "tiktok" ? "hsl(348 84% 56%)" : "hsl(160 84% 36%)"} stopOpacity={1} />
                                            </linearGradient>
                                            {/* Hover variant of projected pattern - denser/brighter stripes */}
                                            <pattern id={`pattern-projected-hover-${chartId}`} patternUnits="userSpaceOnUse" width="6" height="6" patternTransform="rotate(45)">
                                                <rect width="3" height="6" fill={account.platform === "tiktok" ? "hsl(348 79% 56%)" : "hsl(160 70% 42%)"} fillOpacity="0.75" />
                                                <rect x="3" width="3" height="6" fill={account.platform === "tiktok" ? "hsl(348 79% 56%)" : "hsl(160 70% 42%)"} fillOpacity="0.4" />
                                            </pattern>
                                        </defs>

                                        <CartesianGrid vertical={false} strokeDasharray="3 3" className="stroke-gray-100" />
                                        <XAxis dataKey="name" tickLine={false} axisLine={false} tickMargin={8} minTickGap={isMobile ? 50 : 40} tick={{
                  fill: "#9ca3af",
                  fontSize: 12
                }} />
                                        <YAxis hide />
                                        <ChartTooltip cursor={false} content={({
                  active: active_0,
                  payload: payload_0,
                  label: label_2
                }) => {
                  if (!active_0 || !payload_0 || !payload_0.length) return null;
                  const entry_1 = payload_0[0]?.payload;
                  if (!entry_1 || entry_1.type === "empty") return null;

                  // For actual past days - simple tooltip (no image, no caching issue)
                  if (entry_1.type === "actual") {
                    return <div className="rounded-lg border border-gray-200 bg-white px-3 py-2.5 shadow-lg">
                                                            <p className="text-xs font-medium text-gray-500">{label_2}</p>
                                                            <p className="mt-1 text-sm font-bold text-emerald-600">
                                                                +{entry_1.value.toLocaleString()} followers
                                                            </p>
                                                        </div>;
                  }

                  // For today and projected - return null, we render persistent tooltip outside
                  return null;
                }} />

                                        {/* Main bar: actual gains (solid), today (vibrant solid), or projected (striped) */}
                                        <Bar dataKey="value" stackId="forecast" radius={[3, 3, 0, 0]} maxBarSize={24} isAnimationActive={false}>
                                            {forecastData.map((entry_2, index_2) => {
                    const isActive = index_2 === activeBarIndex;
                    let fill: string;
                    if (entry_2.type === "empty") {
                      fill = "transparent";
                    } else if (entry_2.type === "today") {
                      fill = isActive ? `url(#gradient-today-hover-${chartId})` : `url(#gradient-today-${chartId})`;
                    } else if (entry_2.type === "projected") {
                      fill = isActive ? `url(#pattern-projected-hover-${chartId})` : `url(#pattern-projected-${chartId})`;
                    } else {
                      // "actual" type
                      fill = isActive ? `url(#gradient-today-hover-${chartId})` : `url(#gradient-gain-${chartId})`;
                    }
                    return <Cell key={`cell-${index_2}`} fill={fill} style={{
                      transition: "fill-opacity 150ms ease"
                    }} />;
                  })}
                                        </Bar>

                                        {/* Glory extension: faint bar stacked on top showing upgrade potential */}
                                        <Bar dataKey="turboExtra" stackId="forecast" radius={[4, 4, 0, 0]} maxBarSize={24} fill={`url(#gradient-turbo-ext-${chartId})`} isAnimationActive={false} />
                                    </BarChart>
                                </ChartContainer>
                                </div>) : !isAnalyticPage && chartViewMode === "history" ? (/* History Chart - Daily gains bar chart for non-analytics pages */
          !hasHistoryData ? <div className="flex min-h-[220px] flex-col items-center justify-center">
                                        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-gray-100">
                                            <ChartLineIcon className="h-6 w-6 text-gray-400" />
                                        </div>
                                        <p className="mt-3 text-base font-medium text-gray-900">No history yet</p>
                                        <p className="mt-1 text-[13px] text-gray-500">Daily changes show up here as your account grows.</p>
                                    </div> : <ChartContainer config={chartConfig} className="aspect-auto h-[220px] w-full" key={`history-${chartViewMode}`}>
                                        <BarChart data={historyDisplayData} margin={{
              left: 10,
              right: 10,
              top: 10,
              bottom: 0
            }} barCategoryGap={isMobile ? "20%" : "25%"} onMouseMove={state_0 => {
              if (state_0.isTooltipActive) setHistoryHoveredBar(state_0.activeTooltipIndex);else setHistoryHoveredBar(null);
            }} onMouseLeave={() => setHistoryHoveredBar(null)}>
                                            <defs>
                                                <linearGradient id={`gradient-history-gain-${chartId}`} x1="0" y1="0" x2="0" y2="1">
                                                    <stop offset="0%" stopColor={account.platform === "tiktok" ? "hsl(348 79% 56%)" : "hsl(160 70% 42%)"} stopOpacity={1} />
                                                    <stop offset="100%" stopColor={account.platform === "tiktok" ? "hsl(348 79% 56%)" : "hsl(160 70% 35%)"} stopOpacity={0.85} />
                                                </linearGradient>
                                                <linearGradient id={`gradient-history-loss-${chartId}`} x1="0" y1="0" x2="0" y2="1">
                                                    <stop offset="0%" stopColor="hsl(220 10% 75%)" stopOpacity={0.8} />
                                                    <stop offset="100%" stopColor="hsl(220 10% 72%)" stopOpacity={0.5} />
                                                </linearGradient>
                                                <linearGradient id={`gradient-history-loss-hover-${chartId}`} x1="0" y1="0" x2="0" y2="1">
                                                    <stop offset="0%" stopColor="hsl(220 10% 68%)" stopOpacity={0.9} />
                                                    <stop offset="100%" stopColor="hsl(220 10% 72%)" stopOpacity={0.65} />
                                                </linearGradient>
                                                <linearGradient id={`gradient-history-gain-hover-${chartId}`} x1="0" y1="0" x2="0" y2="1">
                                                    <stop offset="0%" stopColor={account.platform === "tiktok" ? "hsl(348 79% 66%)" : "hsl(160 75% 48%)"} stopOpacity={1} />
                                                    <stop offset="100%" stopColor={account.platform === "tiktok" ? "hsl(348 79% 66%)" : "hsl(160 70% 42%)"} stopOpacity={0.9} />
                                                </linearGradient>
                                            </defs>

                                            <CartesianGrid vertical={false} strokeDasharray="3 3" className="stroke-gray-100" />
                                            <XAxis dataKey="name" tickLine={false} axisLine={false} tickMargin={8} minTickGap={isMobile ? 50 : 40} tick={{
                fill: "#9ca3af",
                fontSize: 12
              }} />
                                            <YAxis hide />
                                            <ChartTooltip cursor={false} content={<CustomTooltip />} />

                                            <Bar dataKey="displayGain" radius={[4, 4, 0, 0]} maxBarSize={24} minPointSize={2} isAnimationActive={false}>
                                                {historyDisplayData.map((entry_3, index_3) => {
                  const isNegative = entry_3.displayGain < 0;
                  const isHovered = historyHoveredBar === index_3;
                  return <Cell key={`cell-history-${index_3}`} fill={isNegative ? isHovered ? `url(#gradient-history-loss-hover-${chartId})` : `url(#gradient-history-loss-${chartId})` : isHovered ? `url(#gradient-history-gain-hover-${chartId})` : `url(#gradient-history-gain-${chartId})`} />;
                })}
                                            </Bar>
                                        </BarChart>
                                    </ChartContainer>) : isAnalyticPage && (!chartData || chartData.length === 0) ? <div className="relative min-h-[265px]">
                                    <div className="relative z-10 flex flex-col items-center justify-center pb-14 pt-10">
                                        <p className="text-dashboard-heading font-medium text-gray-600 text-shadow">No data for the selected time range.</p>
                                        <p className="mt-1 px-4 text-dashboard-body text-gray-500">Try selecting a different time range or clearing your date filters.</p>

                                        <button className="shadow-2xs focus:outline-hidden group mt-4 hidden items-center justify-center gap-2 rounded-xl border border-1 border-gray-300 bg-white px-2.5 py-1.5 text-sm font-semibold text-gray-700 shadow-sm transition-all duration-200 hover:bg-gray-50 hover:text-gray-900 lg:inline-flex lg:px-3 lg:py-2" type="button" onClick={() => changeDateRange(null)}>
                                            <TiZoomOut className="h-5 w-5 transform text-green-500 transition-all duration-200 group-hover:-rotate-12" />
                                            Clear Filters
                                        </button>
                                    </div>

                                    <div className="z-9 bg-linear-to-t pointer-events-none absolute left-0 top-0 h-full w-full from-green-200 to-transparent" />
                                </div> : (/* Analytics Page - Bar Chart - Daily/Weekly Follower Gains */
          <ChartContainer config={chartConfig} className="aspect-auto h-[220px] w-full" key={chartTimeFrameTab}>
                                    <BarChart data={chartData} margin={{
              left: 10,
              right: 10,
              top: 10,
              bottom: 0
            }} onMouseMove={state_1 => {
              if (state_1.isTooltipActive) setHoveredBar(state_1.activeTooltipIndex);else setHoveredBar(null);
            }}>
                                        <defs>
                                            {/* Gradient for positive gains - signature green */}
                                            <linearGradient id={`gradient-gain-${chartId}`} x1="0" y1="0" x2="0" y2="1">
                                                <stop offset="0%" stopColor={account.platform === "tiktok" ? "hsl(348 79% 56%)" : "hsl(160 70% 42%)"} stopOpacity={1} />
                                                <stop offset="100%" stopColor={account.platform === "tiktok" ? "hsl(348 79% 56%)" : "hsl(160 70% 35%)"} stopOpacity={0.85} />
                                            </linearGradient>
                                            {/* Gradient for negative gains - neutral gray */}
                                            <linearGradient id={`gradient-loss-${chartId}`} x1="0" y1="0" x2="0" y2="1">
                                                <stop offset="0%" stopColor="hsl(220 10% 75%)" stopOpacity={0.8} />
                                                <stop offset="100%" stopColor="hsl(220 10% 72%)" stopOpacity={0.5} />
                                            </linearGradient>
                                            {/* Hover state - brighter green */}
                                            <linearGradient id={`gradient-gain-hover-${chartId}`} x1="0" y1="0" x2="0" y2="1">
                                                <stop offset="0%" stopColor={account.platform === "tiktok" ? "hsl(348 79% 66%)" : "hsl(160 75% 48%)"} stopOpacity={1} />
                                                <stop offset="100%" stopColor={account.platform === "tiktok" ? "hsl(348 79% 66%)" : "hsl(160 70% 42%)"} stopOpacity={0.9} />
                                            </linearGradient>
                                            {/* Hover state - slightly darker gray for negative bars */}
                                            <linearGradient id={`gradient-loss-hover-${chartId}`} x1="0" y1="0" x2="0" y2="1">
                                                <stop offset="0%" stopColor="hsl(220 10% 68%)" stopOpacity={0.9} />
                                                <stop offset="100%" stopColor="hsl(220 10% 72%)" stopOpacity={0.65} />
                                            </linearGradient>
                                        </defs>

                                        <CartesianGrid vertical={false} strokeDasharray="3 3" className="stroke-gray-100" />
                                        <XAxis dataKey="name" tickLine={false} axisLine={false} tickMargin={8} minTickGap={32} tick={{
                fill: "#9ca3af",
                fontSize: 12
              }} />
                                        <YAxis hide />
                                        <ChartTooltip cursor={false} content={<CustomTooltip />} />

                                        <Bar dataKey="gain" radius={[4, 4, 0, 0]} maxBarSize={60} minPointSize={2} onMouseEnter={() => setIsHoveringChart(true)} onMouseLeave={() => setIsHoveringChart(false)}>
                                            {chartData.map((entry_4, index_4) => {
                  const isNegative_0 = entry_4.gain < 0;
                  const isHovered_0 = hoveredBar === index_4;
                  return <Cell key={`cell-${index_4}`} fill={isNegative_0 ? isHovered_0 ? `url(#gradient-loss-hover-${chartId})` : `url(#gradient-loss-${chartId})` : isHovered_0 ? `url(#gradient-gain-hover-${chartId})` : `url(#gradient-gain-${chartId})`} />;
                })}
                                        </Bar>
                                    </BarChart>
                                </ChartContainer>)}
                        </div>
                    </div>
                </div>)}
            
            {/* Portal tooltip - always mounted, hidden via CSS to prevent image refetch on re-hover */}
            {/* Entrance: smooth fade-in + slide-up | Bar-to-bar: instant position updates | Exit: quick fade */}
            {typeof document !== "undefined" && chartViewMode === "forecast" && tooltipPos && createPortal(<div className="pointer-events-none fixed z-[9999]" style={{
      // Position - instant updates (no transition on left/top)
      left: tooltipPos.x,
      top: tooltipPos.y,
      // GPU-accelerated transform for smooth animation
      // Entrance: slide up 8px | Visible: at position | Hidden: ready for next entrance
      transform: `translate3d(-50%, calc(-100% + ${isTooltipVisible ? "0px" : "8px"}), 0)`,
      opacity: isTooltipVisible ? 1 : 0,
      visibility: isTooltipVisible ? "visible" : "hidden",
      // Entrance animation only - bar-to-bar is instant via transform
      transition: isTooltipVisible ? "opacity 0.2s cubic-bezier(0.16, 1, 0.3, 1), transform 0.2s cubic-bezier(0.16, 1, 0.3, 1)" : "opacity 0.15s ease-out, transform 0.15s ease-out, visibility 0s linear 0.15s",
      willChange: "transform, opacity"
    }}>
                    <ProfilePreviewTooltip avatarUrl={profileData.avatarUrl} username={profileData.username} fullName={profileData.fullName} postsCount={profileData.postsCount} followersCount={activeBarIndex !== null && forecastData[activeBarIndex] ? forecastData[activeBarIndex].projectedTotal : 0} followingCount={profileData.followingCount} dateLabel={activeBarIndex !== null && forecastData[activeBarIndex] ? forecastData[activeBarIndex].type === "today" ? "Today's projected total" : forecastData[activeBarIndex].name : ""} isVerified={profileData.isVerified} dailyGain={activeBarIndex !== null && forecastData[activeBarIndex] ? forecastData[activeBarIndex].value : 0} turboTotal={activeBarIndex !== null && forecastData[activeBarIndex] ? forecastData[activeBarIndex].turboTotal : 0} showTurboUpsell={!["turbo", "glory"].includes(account.account_type || "")} entryType={activeBarIndex !== null && forecastData[activeBarIndex] ? forecastData[activeBarIndex].type as "today" | "projected" : "projected"} />
                </div>, document.body)}
        </>;
}